import { Interval } from '@models/graph';

import { roundPrecision } from '@utils/conversion';
import { formatDuration } from '@utils/format';

import { HealthScoreType } from './health-score-info';
import type { StackedIconProps } from './scan';

// FITNESS & SLEEP
export const AVG_HEART_RATE = 'average-heart-rate';

// VITAL SIGN
export const BLOOD_PRESSURE = 'blood-pressure';
export const SYSTOLIC = 'systolic';
export const DIASTOLIC = 'diastolic';

export const HEART_RATE = 'heart-rate';
export const OXYGEN_SATURATION = 'oxygen-saturation';
export const PAIN = 'pain';
export const RESPIRATION = 'respiration';
export const BODY_TEMPERATURE = 'temperature';
// BODY
export const BLOOD_GLUCOSE = 'blood-sugar';
export const BLOOD_TYPE = 'blood-type';
export const BODY_HEIGHT = 'body-height';
export const BODY_MASS_INDEX = 'body-mass-index';
export const BODY_WEIGHT = 'body-weight';
export const MUSCLE_MASS = 'muscle-mass';
// FITNESS
export const ENERGY_BURNED = 'energy-burned';
export const DISTANCE = 'distance';
export const RESTING_HEART_RATE = 'resting-heart-rate';
export const STEPS = 'steps';
// SLEEP
export const SLEEP_DURATION = 'sleep-duration';
export const SLEEP_SCORE = 'sleep-score';

// FACE SCAN
export const STRESS_INDEX = 'stress-index';
export const HR_VARIABILITY = 'hrv';
export const IRREGULAR_HR = 'irregular-heart-beat';
export const CARDIAC_WORKLOAD = 'cardiac-workload';

// BODY SCAN
export const BODY_FAT_PERCENTAGE = 'body-fat-percentage';
export const HIPS_CM = 'body-hips-cm';
export const WAIST_CM = 'body-waist-cm';
export const CHEST_CM = 'body-chest-cm';
export const THIGH_CM = 'body-thigh-cm';
export const WAIST_HIP_RATIO = 'body-waist-hip';
export const WAIST_HEIGHT_RATIO = 'body-waist-height';

// BHA
export const ANXIETY = 'anxiety';
export const ARTERIAL_STIFFNESS = 'arterial-stiffness';
export const CARDIORESPIRATORY_FITNESS = 'cardiorespiratory-fitness';
export const CHOLESTEROL = 'totalcholesterol';
export const C_REACTIVE_PROTEIN_I = 'systemic-low-grade-inflammation-index';
export const DEPRESSION = 'depression';
export const FRAMINGHAM_SCORE = 'framingham-score';
export const HBA1C = 'hgba1c';
export const HDL_CHOL = 'hdl';
export const LIPID_ACCUMULATION_PRODUCT = 'lipid-accumulation-product';
export const LDL_CHOL = 'ldl';
export const MET_S_DEGREE = 'metabolic-health-risk';
export const TEN_YEAR_CVD_RISK = '10-year-cardiovascular-disease-risk';
export const TRIGLYCERIDES = 'triglycerides';
export const VISCERAL_ADIPOSITY_INDEX = 'visceral-adiposity-index';

enum LabCodes {
  PATHOLOGY = 'pathology',
  OTHER_URINE_ANALYSES = 'other_urine_analyses',
  MICROBIOLOGY = 'microbiology',
  CBC = 'cbc',
  LIPIDS = 'lipids',
  URINALYSIS = 'urinalysis',
  LIVER_FUNCTION = 'liver_function',
  CHEMISTRY = 'chemistry',
  GENETICS = 'genetics',
  OTHER = 'other',
  THYROID = 'thyroid',
  BLOOD_TRANSFUSION = 'blood_transfusion',
  COAGULATION = 'coagulation',
  CARDIAC = 'cardiac',
  ELECTROLYTES = 'electrolytes',
  ARTERIAL_BLOOD_GASES = 'arterial_blood_gases',
}

// keys, unit, multiplier, precision, key, messageId
export type SignType = {
  keys: {
    validic: string;
    vitall: string;
    common: string;
    loinc?: string;
    snomed?: string;
    icd10?: string;
  }[];
  unit: string;
  icon: StackedIconProps[];
  multiplier: number;
  precision: number;
  key: string;
  messageId: string;
  aggregate?: string;
  format?: (value: number | string, unit?: string | null) => string;
  graph?: {
    showTime: boolean;
    graphInterval?: Interval;
    graphRange?: number;
  };
};
const baseFields = {
  unit: '',
  icon: [],
  multiplier: 1,
  precision: 0,
};
const BHA_METRIC_TYPES: Record<string, SignType> = {
  [ANXIETY]: {
    keys: [
      {
        validic: ANXIETY,
        vitall: ANXIETY,
        common: ANXIETY,
      },
    ],
    key: ANXIETY,
    messageId: 'bhaResults.anxiety.title',
    ...baseFields,
  },
  [ARTERIAL_STIFFNESS]: {
    keys: [
      {
        validic: ARTERIAL_STIFFNESS,
        vitall: ARTERIAL_STIFFNESS,
        common: ARTERIAL_STIFFNESS,
      },
    ],
    key: ARTERIAL_STIFFNESS,
    messageId: 'bhaResults.arterialStiffness.title',
    ...baseFields,
  },
  [C_REACTIVE_PROTEIN_I]: {
    keys: [
      {
        validic: C_REACTIVE_PROTEIN_I,
        vitall: C_REACTIVE_PROTEIN_I,
        common: C_REACTIVE_PROTEIN_I,
      },
    ],
    unit: '',
    icon: [],
    multiplier: 1,
    precision: 0,
    key: C_REACTIVE_PROTEIN_I,
    messageId: 'bhaResults.crpi.title',
    format: (value: number | string) =>
      String(typeof value === 'number' ? Math.round(value).toString() : value),
  },
  [CARDIORESPIRATORY_FITNESS]: {
    keys: [
      {
        validic: CARDIORESPIRATORY_FITNESS,
        vitall: CARDIORESPIRATORY_FITNESS,
        common: CARDIORESPIRATORY_FITNESS,
      },
    ],
    key: CARDIORESPIRATORY_FITNESS,
    messageId: 'bhaResults.cardioFitness.title',
    ...baseFields,
  },
  [CHOLESTEROL]: {
    keys: [
      {
        validic: CHOLESTEROL,
        vitall: CHOLESTEROL,
        common: CHOLESTEROL,
      },
    ],
    key: CHOLESTEROL,
    messageId: 'bhaResults.cholesterol.title',
    ...baseFields,
  },
  [DEPRESSION]: {
    keys: [
      {
        validic: DEPRESSION,
        vitall: DEPRESSION,
        common: DEPRESSION,
      },
    ],
    key: DEPRESSION,
    messageId: 'bhaResults.despression.title',
    ...baseFields,
  },
  [FRAMINGHAM_SCORE]: {
    keys: [
      {
        validic: FRAMINGHAM_SCORE,
        vitall: FRAMINGHAM_SCORE,
        common: FRAMINGHAM_SCORE,
      },
    ],
    key: FRAMINGHAM_SCORE,
    messageId: 'bhaResults.framinghamScore.title',
    ...baseFields,
  },
  [HBA1C]: {
    keys: [
      {
        validic: HBA1C,
        vitall: HBA1C,
        common: HBA1C,
      },
    ],
    unit: '%',
    icon: [],
    multiplier: 1,
    precision: 0,
    key: VISCERAL_ADIPOSITY_INDEX,
    messageId: 'bhaResults.hba1c.title',
    format: (value: number | string) => String(parseInt(value.toString())),
  },
  [HDL_CHOL]: {
    keys: [
      {
        validic: HDL_CHOL,
        vitall: HDL_CHOL,
        common: HDL_CHOL,
      },
    ],
    key: HDL_CHOL,
    messageId: 'bhaResults.hdl.title',
    ...baseFields,
  },
  [LDL_CHOL]: {
    keys: [
      {
        validic: LDL_CHOL,
        vitall: LDL_CHOL,
        common: LDL_CHOL,
      },
    ],
    key: LDL_CHOL,
    messageId: 'bhaResults.ldl.title',
    ...baseFields,
  },
  [LIPID_ACCUMULATION_PRODUCT]: {
    keys: [
      {
        validic: LIPID_ACCUMULATION_PRODUCT,
        vitall: LIPID_ACCUMULATION_PRODUCT,
        common: LIPID_ACCUMULATION_PRODUCT,
      },
    ],
    unit: '',
    icon: [],
    multiplier: 1,
    precision: 1,
    key: LIPID_ACCUMULATION_PRODUCT,
    messageId: 'bhaResults.lap.title',
    format: (value: number | string) =>
      String(typeof value === 'number' ? value.toFixed(1) : value),
  },
  [MET_S_DEGREE]: {
    keys: [
      {
        validic: MET_S_DEGREE,
        vitall: MET_S_DEGREE,
        common: MET_S_DEGREE,
      },
    ],
    unit: '',
    icon: [],
    multiplier: 1,
    precision: 0,
    key: MET_S_DEGREE,
    messageId: 'bhaResults.metsDegree.title',
    format: (value: number | string) =>
      String(typeof value === 'number' ? Math.round(value) : value),
  },
  [TEN_YEAR_CVD_RISK]: {
    keys: [
      {
        validic: TEN_YEAR_CVD_RISK,
        vitall: TEN_YEAR_CVD_RISK,
        common: TEN_YEAR_CVD_RISK,
      },
    ],
    key: TEN_YEAR_CVD_RISK,
    messageId: 'bhaResults.tenYrCvdRisk.title',
    ...baseFields,
  },
  [TRIGLYCERIDES]: {
    keys: [
      {
        validic: TRIGLYCERIDES,
        vitall: TRIGLYCERIDES,
        common: TRIGLYCERIDES,
      },
    ],
    key: TRIGLYCERIDES,
    messageId: 'bhaResults.triglycerides.title',
    ...baseFields,
  },
  [VISCERAL_ADIPOSITY_INDEX]: {
    keys: [
      {
        validic: VISCERAL_ADIPOSITY_INDEX,
        vitall: VISCERAL_ADIPOSITY_INDEX,
        common: VISCERAL_ADIPOSITY_INDEX,
      },
    ],
    unit: '',
    icon: [],
    multiplier: 1,
    precision: 1,
    key: VISCERAL_ADIPOSITY_INDEX,
    messageId: 'bhaResults.vai.title',
    format: (value: number | string) =>
      String(typeof value === 'number' ? value.toFixed(1) : value),
  },
};

const HEALTH_SCORE_TYPES: Record<string, SignType> = {
  /*
  [HealthScoreType.Cardiovascular]: {
    keys: [
      {
        validic: HealthScoreType.Cardiovascular,
        vitall: HealthScoreType.Cardiovascular,
        common: HealthScoreType.Cardiovascular,
      },
    ],
    key: HealthScoreType.Cardiovascular,
    messageId: 'healthScores.cardiovascular.title',
    ...baseFields,
  },
  [HealthScoreType.Focus]: {
    keys: [
      {
        validic: HealthScoreType.Focus,
        vitall: HealthScoreType.Focus,
        common: HealthScoreType.Focus,
      },
    ],
    key: HealthScoreType.Focus,
    messageId: 'healthScores.focus.title',
    ...baseFields,
  },
  */
  [HealthScoreType.Immune]: {
    keys: [
      {
        validic: HealthScoreType.Immune,
        vitall: HealthScoreType.Immune,
        common: HealthScoreType.Immune,
      },
    ],
    key: HealthScoreType.Immune,
    messageId: 'healthScores.immune.title',
    ...baseFields,
  },
  [HealthScoreType.Respiratory]: {
    keys: [
      {
        validic: HealthScoreType.Respiratory,
        vitall: HealthScoreType.Respiratory,
        common: HealthScoreType.Respiratory,
      },
    ],
    key: HealthScoreType.Respiratory,
    messageId: 'healthScores.respiratory.title',
    ...baseFields,
  },
  [HealthScoreType.Sleep]: {
    keys: [
      {
        validic: HealthScoreType.Sleep,
        vitall: HealthScoreType.Sleep,
        common: HealthScoreType.Sleep,
      },
    ],
    key: HealthScoreType.Sleep,
    messageId: 'healthScores.sleep.title',
    ...baseFields,
  },
  [HealthScoreType.Stress]: {
    keys: [
      {
        validic: HealthScoreType.Stress,
        vitall: HealthScoreType.Stress,
        common: HealthScoreType.Stress,
      },
    ],
    key: HealthScoreType.Stress,
    messageId: 'healthScores.stress.title',
    ...baseFields,
  },
};

export const VITAL_SIGN_TYPES: Record<string, SignType> = {
  [AVG_HEART_RATE]: {
    keys: [
      {
        validic: 'avg_heart_rate',
        vitall: 'heart-rate',
        common: AVG_HEART_RATE,
      },
    ],
    unit: 'bpm',
    icon: [{ name: 'heart-pulse', color: 'primary.main' }],
    multiplier: 1,
    precision: 0,
    key: AVG_HEART_RATE,
    messageId: 'vitalSigns.avgHeartRate',
    graph: {
      showTime: false,
      graphInterval: Interval.Month,
      graphRange: 1,
    },
  },
  [BLOOD_GLUCOSE]: {
    keys: [
      {
        validic: 'blood_glucose',
        vitall: 'blood-sugar',
        common: BLOOD_GLUCOSE,
      },
    ],
    unit: 'mmol/L',
    icon: [{ name: 'droplet', color: 'primary.main' }],
    multiplier: 1,
    precision: 2,
    key: BLOOD_GLUCOSE,
    messageId: 'vitalSigns.bloodSugar',
    format: (value: number | string, unit?: string | null) => {
      let precision = 0;
      if (unit === 'mmol/L') {
        precision = 1;
      }
      return String(
        typeof value === 'number' ? roundPrecision(value, precision) : value
      );
    },
    graph: {
      showTime: true,
      graphInterval: Interval.Day,
      graphRange: 3,
    },
  },
  [BODY_HEIGHT]: {
    keys: [
      {
        validic: 'body_height',
        vitall: 'body-height',
        common: BODY_HEIGHT,
      },
    ],
    unit: 'm',
    icon: [{ name: 'ruler-vertical', color: 'primary.main' }],
    multiplier: 1,
    precision: 2,
    format: (value: number | string, unit?: string | null) => {
      if (unit === 'ft/in') {
        // Round the total first, then split. Rounding the inches remainder
        // instead would let 71.6in render as "5' 12"".
        const inches_total = Math.round(Number(value));
        if (!Number.isFinite(inches_total)) {
          return '- -';
        }
        const feet = Math.floor(inches_total / 12);
        const inches = inches_total % 12;
        return `${feet}' ${inches}"`;
      }
      return String(typeof value === 'number' ? value.toFixed(2) : value);
    },
    key: BODY_HEIGHT,
    messageId: 'vitalSigns.bodyHeight',
    graph: {
      showTime: false,
      graphInterval: Interval.Year,
      graphRange: 5,
    },
  },
  [BODY_TEMPERATURE]: {
    keys: [
      {
        validic: 'body_temperature',
        vitall: 'temperature',
        common: BODY_TEMPERATURE,
      },
    ],
    unit: '°C',
    icon: [{ name: 'temperature-half', color: 'primary.main' }],
    multiplier: 1,
    precision: 1,
    format: (value: number | string) =>
      String(typeof value === 'number' ? value.toFixed(1) : value),
    key: BODY_TEMPERATURE,
    messageId: 'vitalSigns.bodyTemperature',
    graph: {
      showTime: false,
      graphInterval: Interval.Year,
      graphRange: 1,
    },
  },
  [BODY_WEIGHT]: {
    keys: [
      {
        validic: 'body_weight',
        vitall: 'body-weight',
        common: BODY_WEIGHT,
      },
    ],
    unit: 'kg',
    icon: [{ name: 'weight-scale', color: 'secondary.main' }],
    multiplier: 1,
    precision: 2,
    format: (value: number | string) =>
      String(typeof value === 'number' ? value.toFixed(2) : value),
    key: BODY_WEIGHT,
    messageId: 'vitalSigns.bodyWeight',
    graph: {
      showTime: false,
      graphInterval: Interval.Year,
      graphRange: 1,
    },
  },
  [BLOOD_PRESSURE]: {
    keys: [
      {
        validic: 'systolic',
        vitall: 'systolic',
        common: `systolic`,
        loinc: '8480-6',
        snomed: '271649006',
        icd10: 'Z01.30',
      },
      {
        validic: 'diastolic',
        vitall: 'diastolic',
        common: `diastolic`,
        loinc: '8462-4',
        snomed: '271650006',
        icd10: 'Z01.30',
      },
    ],
    unit: 'mmHg',
    icon: [{ name: 'gauge', color: 'primary.main' }],
    multiplier: 1,
    precision: 0,
    key: BLOOD_PRESSURE,
    messageId: 'vitalSigns.bloodPressure',
    graph: {
      showTime: true,
      graphInterval: Interval.Month,
      graphRange: 1,
    },
  },
  [DISTANCE]: {
    keys: [{ validic: 'distance', vitall: 'distance', common: DISTANCE }],
    unit: 'km',
    icon: [{ name: 'ruler-horizontal', color: 'primary.main' }],
    multiplier: 1,
    precision: 2,
    format: (value: number | string) =>
      String(typeof value === 'number' ? value.toFixed(2) : value),
    aggregate: 'MAX',
    key: DISTANCE,
    messageId: 'vitalSigns.distance',
    graph: {
      showTime: false,
      graphInterval: Interval.Week,
      graphRange: 1,
    },
  },
  [ENERGY_BURNED]: {
    keys: [{ validic: 'energy_burned', vitall: '', common: ENERGY_BURNED }],
    unit: 'cal',
    icon: [{ name: 'fire-flame-curved', color: 'primary.main' }],
    multiplier: 1.0,
    precision: 0,
    aggregate: 'MAX',
    key: ENERGY_BURNED,
    messageId: 'vitalSigns.energyBurned',
    graph: {
      showTime: false,
      graphInterval: Interval.Week,
      graphRange: 1,
    },
  },
  [HEART_RATE]: {
    keys: [{ validic: 'pulse', vitall: 'heart-rate', common: HEART_RATE }],
    unit: 'bpm',
    icon: [{ name: 'heart-pulse', color: 'primary.main' }],
    multiplier: 1,
    precision: 0,
    key: HEART_RATE,
    messageId: 'vitalSigns.heartRate',
    graph: {
      showTime: false,
      graphInterval: Interval.Month,
      graphRange: 1,
    },
  },
  [RESPIRATION]: {
    keys: [
      {
        validic: '',
        vitall: 'respiration',
        common: RESPIRATION,
      },
    ],
    unit: 'bpm',
    icon: [{ name: 'lungs', color: 'primary.main' }],
    multiplier: 1,
    precision: 0,
    key: RESPIRATION,
    messageId: 'vitalSigns.respiration',
    graph: {
      showTime: false,
      graphInterval: Interval.Month,
      graphRange: 1,
    },
  },
  [STEPS]: {
    keys: [{ validic: 'steps', vitall: '', common: STEPS }],
    unit: 'steps',
    icon: [{ name: 'shoe-prints', color: 'primary.main' }],
    multiplier: 1,
    precision: 0,
    aggregate: 'MAX',
    key: STEPS,
    messageId: 'vitalSigns.steps',
    graph: {
      showTime: false,
      graphInterval: Interval.Week,
      graphRange: 1,
    },
  },
  [SLEEP_DURATION]: {
    keys: [
      {
        validic: 'sleep_duration',
        vitall: '',
        common: SLEEP_DURATION,
      },
    ],
    unit: 'h',
    icon: [{ name: 'alarm-snooze', color: 'primary.main' }],
    multiplier: 1,
    precision: 2,
    format: formatDuration,
    key: SLEEP_DURATION,
    messageId: 'vitalSigns.sleepDuration',
    graph: {
      showTime: false,
      graphInterval: Interval.Month,
      graphRange: 1,
    },
  },
  [SLEEP_SCORE]: {
    keys: [
      {
        validic: 'sleep_score',
        vitall: '',
        common: SLEEP_SCORE,
      },
    ],
    unit: '%',
    icon: [{ name: 'face-sleeping', color: 'primary.main' }],
    multiplier: 1,
    precision: 0,
    key: SLEEP_SCORE,
    messageId: 'vitalSigns.sleepScore',
    graph: {
      showTime: false,
      graphInterval: Interval.Month,
      graphRange: 1,
    },
  },
  [OXYGEN_SATURATION]: {
    keys: [
      {
        validic: 'spo2',
        vitall: 'oxygen-saturation',
        common: OXYGEN_SATURATION,
      },
    ],
    unit: '%',
    icon: [
      {
        name: 'circle',
        color: 'primary.main',
        transform: { x: -1, y: -1, size: 15 },
        type: 'far',
        style: { opacity: 0.5 },
      },
      {
        name: '2',
        color: 'primary.main',
        transform: { x: 6, y: 6, size: 5 },
        type: 'fas',
      },
    ],
    // icon: (
    //   <span className="fa-layers fa-fw">
    //     <FontAwesomeIcon
    //       icon={{ prefix: 'far', iconName: 'circle' }}
    //       style={{ color: 'orange.main', opacity: 0.5 }}
    //       transform={{ x: -1, y: -1, size: 15 }}
    //     />
    //     <FontAwesomeIcon
    //       icon={{ prefix: 'fas', iconName: '2' }}
    //       style={{ color: 'orange.main' }}
    //       transform={{ x: 6, y: 6, size: 5 }}
    //     />
    //   </span>
    // ),
    multiplier: 1,
    precision: 0,
    key: OXYGEN_SATURATION,
    messageId: 'vitalSigns.oxygenSaturation',
    graph: {
      showTime: false,
      graphInterval: Interval.Month,
      graphRange: 1,
    },
  },
  [PAIN]: {
    keys: [{ validic: '', vitall: 'pain', common: PAIN }],
    unit: '',
    icon: [{ name: 'face-smile', color: 'primary.main' }],
    multiplier: 1,
    precision: 0,
    key: PAIN,
    messageId: 'vitalSigns.pain',
    graph: {
      showTime: false,
      graphInterval: Interval.Year,
      graphRange: 1,
    },
  },
  [BLOOD_TYPE]: {
    keys: [
      {
        validic: '',
        vitall: 'blood-type',
        common: BLOOD_TYPE,
      },
    ],
    unit: '',
    icon: [{ name: 'fire-flame', color: 'primary.main' }],
    multiplier: 1,
    precision: 0,
    key: BLOOD_TYPE,
    messageId: 'vitalSigns.bloodType',
  },
  [BODY_MASS_INDEX]: {
    keys: [
      {
        validic: 'body_mass_index',
        vitall: 'body-mass-index',
        common: BODY_MASS_INDEX,
      },
    ],
    unit: '',
    icon: [{ name: 'person', color: 'primary.main' }],
    multiplier: 1,
    precision: 1,
    key: BODY_MASS_INDEX,
    format: (value: number | string) =>
      String(
        typeof value === 'number'
          ? Number.isInteger(value)
            ? value
            : Math.round(value)
          : value
      ),
    messageId: 'vitalSigns.bodyMassIndex',
    graph: {
      showTime: false,
      graphInterval: Interval.Year,
      graphRange: 1,
    },
  },
  [RESTING_HEART_RATE]: {
    keys: [
      {
        validic: 'resting_heart_rate',
        vitall: 'resting-heart-rate',
        common: RESTING_HEART_RATE,
      },
    ],
    unit: 'bpm',
    icon: [{ name: 'heart-pulse', color: 'primary.main' }],
    multiplier: 1,
    precision: 0,
    key: RESTING_HEART_RATE,
    messageId: 'vitalSigns.restingHeartRate',
    graph: {
      showTime: false,
      graphInterval: Interval.Month,
      graphRange: 1,
    },
  },
  [MUSCLE_MASS]: {
    keys: [
      {
        validic: 'muscle_mass',
        vitall: 'muscle-mass',
        common: MUSCLE_MASS,
      },
    ],
    unit: 'kg',
    icon: [{ name: 'dumbbell', color: 'primary.main' }],
    multiplier: 1,
    precision: 1,
    key: MUSCLE_MASS,
    format: (value: number | string) =>
      String(typeof value === 'number' ? value.toFixed(1) : value),
    messageId: 'vitalSigns.bodyMuscleMass',
    graph: {
      showTime: false,
      graphInterval: Interval.Year,
      graphRange: 1,
    },
  },
  [HR_VARIABILITY]: {
    keys: [
      {
        validic: HR_VARIABILITY,
        vitall: HR_VARIABILITY,
        common: HR_VARIABILITY,
      },
    ],
    key: HR_VARIABILITY,
    unit: 'ms',
    icon: [{ name: 'wave-pulse', color: 'primary.main' }],
    messageId: 'scanMetrics.face.hrVariability',
    multiplier: 1,
    precision: 1,
    format: (value: number | string) => String(parseInt(value.toString())),
    graph: {
      showTime: false,
      graphInterval: Interval.Month,
      graphRange: 1,
    },
  },
  [IRREGULAR_HR]: {
    keys: [
      {
        validic: IRREGULAR_HR,
        vitall: IRREGULAR_HR,
        common: IRREGULAR_HR,
      },
    ],
    key: IRREGULAR_HR,
    unit: '',
    icon: [{ name: 'monitor-waveform', color: 'primary.main' }],
    messageId: 'scanMetrics.face.irregularHr',
    multiplier: 1,
    precision: 1,
  },
  [CARDIAC_WORKLOAD]: {
    keys: [
      {
        validic: CARDIAC_WORKLOAD,
        vitall: CARDIAC_WORKLOAD,
        common: CARDIAC_WORKLOAD,
      },
    ],
    key: CARDIAC_WORKLOAD,
    unit: 'dB',
    icon: [{ name: 'heart', color: 'primary.main' }],
    messageId: 'scanMetrics.face.cardiacWorkload',
    format: (value: number | string) =>
      String(typeof value === 'number' ? value.toFixed(2) : value),
    multiplier: 1,
    precision: 2,
  },
  [STRESS_INDEX]: {
    keys: [
      {
        validic: STRESS_INDEX,
        vitall: STRESS_INDEX,
        common: STRESS_INDEX,
      },
    ],
    key: STRESS_INDEX,
    unit: '',
    icon: [{ name: 'gauge-circle-bolt', color: 'primary.main' }],
    messageId: 'scanMetrics.face.stressIndex',
    multiplier: 1,
    precision: 2,
    graph: {
      showTime: false,
      graphInterval: Interval.Week,
      graphRange: 1,
    },
  },
  // Body scan
  [BODY_FAT_PERCENTAGE]: {
    keys: [
      {
        validic: BODY_FAT_PERCENTAGE,
        vitall: BODY_FAT_PERCENTAGE,
        common: BODY_FAT_PERCENTAGE,
      },
    ],
    key: BODY_FAT_PERCENTAGE,
    unit: '%',
    icon: [{ name: 'person', color: 'secondary.main' }],
    messageId: 'scanMetrics.body.fatPercentage',
    multiplier: 1,
    precision: 2,
    format: (value: number | string) =>
      String(typeof value === 'number' ? Math.round(value) : value),
    graph: {
      showTime: false,
      graphInterval: Interval.Year,
      graphRange: 1,
    },
  },
  [CHEST_CM]: {
    keys: [
      {
        validic: CHEST_CM,
        vitall: CHEST_CM,
        common: CHEST_CM,
      },
    ],
    key: CHEST_CM,
    unit: 'cm',
    icon: [
      { name: 'person', color: 'secondary.main' },
      {
        name: 'hyphen',
        color: 'secondary.light',
        transform: {
          y: -3,
          x: 2,
        },
        size: 'lg',
        type: 'fas',
      },
    ],
    messageId: 'scanMetrics.body.chestCm',
    format: (value: number | string) =>
      String(typeof value === 'number' ? value.toFixed(2) : value),
    multiplier: 1,
    precision: 2,
    graph: {
      showTime: false,
      graphInterval: Interval.Year,
      graphRange: 1,
    },
  },
  [WAIST_CM]: {
    keys: [
      {
        validic: WAIST_CM,
        vitall: WAIST_CM,
        common: WAIST_CM,
      },
    ],
    key: WAIST_CM,
    unit: 'cm',
    icon: [
      { name: 'person', color: 'secondary.main' },
      {
        name: 'hyphen',
        color: 'secondary.light',
        transform: {
          y: 1,
          x: 2,
        },
        size: 'lg',
        type: 'fas',
      },
    ],
    messageId: 'scanMetrics.body.waistCm',
    format: (value: number | string) =>
      String(typeof value === 'number' ? value.toFixed(2) : value),
    multiplier: 1,
    precision: 2,
    graph: {
      showTime: false,
      graphInterval: Interval.Year,
      graphRange: 1,
    },
  },
  [HIPS_CM]: {
    keys: [
      {
        validic: HIPS_CM,
        vitall: HIPS_CM,
        common: HIPS_CM,
      },
    ],
    key: HIPS_CM,
    unit: 'cm',
    icon: [
      { name: 'person', color: 'secondary.main' },
      {
        name: 'hyphen',
        color: 'secondary.light',
        transform: {
          y: 3,
          x: 2,
        },
        size: 'lg',
        type: 'fas',
      },
    ],
    messageId: 'scanMetrics.body.hipsCm',
    format: (value: number | string) =>
      String(typeof value === 'number' ? value.toFixed(2) : value),
    multiplier: 1,
    precision: 2,
    graph: {
      showTime: false,
      graphInterval: Interval.Year,
      graphRange: 1,
    },
  },
  [THIGH_CM]: {
    keys: [
      {
        validic: THIGH_CM,
        vitall: THIGH_CM,
        common: THIGH_CM,
      },
    ],
    key: THIGH_CM,
    unit: 'cm',
    icon: [
      { name: 'person', color: 'secondary.main' },
      {
        name: 'hyphen',
        color: 'secondary.light',
        transform: {
          y: 5,
          x: 2,
        },
        size: 'lg',
        type: 'fas',
      },
    ],
    messageId: 'scanMetrics.body.thighCm',
    format: (value: number | string) =>
      String(typeof value === 'number' ? value.toFixed(2) : value),
    multiplier: 1,
    precision: 2,
    graph: {
      showTime: false,
      graphInterval: Interval.Year,
      graphRange: 1,
    },
  },
  [WAIST_HIP_RATIO]: {
    keys: [
      {
        validic: WAIST_HIP_RATIO,
        vitall: WAIST_HIP_RATIO,
        common: WAIST_HIP_RATIO,
      },
    ],
    key: WAIST_HIP_RATIO,
    unit: '',
    icon: [
      {
        name: 'hyphen',
        color: 'secondary.main',
        transform: {
          y: -2.5,
        },
      },
      {
        name: 'dash',
        color: 'secondary.main',
        transform: {
          y: 2.5,
        },
        type: 'fas',
      },
    ],
    messageId: 'scanMetrics.body.waistHipRatio',
    format: (value: number | string) =>
      String(typeof value === 'number' ? value.toFixed(2) : value),
    multiplier: 1,
    precision: 2,
    graph: {
      showTime: false,
      graphInterval: Interval.Year,
      graphRange: 1,
    },
  },
  [WAIST_HEIGHT_RATIO]: {
    keys: [
      {
        validic: WAIST_HEIGHT_RATIO,
        vitall: WAIST_HEIGHT_RATIO,
        common: WAIST_HEIGHT_RATIO,
      },
    ],
    key: WAIST_HEIGHT_RATIO,
    unit: '',
    icon: [
      {
        name: 'hyphen',
        color: 'secondary.main',
        transform: {
          rotate: 90,
          x: -2.5,
        },
      },
      {
        name: 'dash',
        color: 'secondary.main',
        transform: {
          rotate: 90,
          x: 2.5,
        },
        type: 'fas',
      },
    ],
    messageId: 'scanMetrics.body.waistHeightRatio',
    format: (value: number | string) =>
      String(typeof value === 'number' ? value.toFixed(2) : value),
    multiplier: 1,
    precision: 2,
    graph: {
      showTime: false,
      graphInterval: Interval.Year,
      graphRange: 1,
    },
  },
  // BHA
  ...BHA_METRIC_TYPES,
  // Health scores
  ...HEALTH_SCORE_TYPES,
};

export const FHIR_CODES: Record<
  string,
  Record<
    string,
    { messageId: string; icon?: React.ReactNode; group?: LabCodes[] }
  >
> = {
  'lab-results': {
    [LabCodes.PATHOLOGY]: {
      messageId: 'labResults.pathology',
    },
    [LabCodes.OTHER_URINE_ANALYSES]: {
      messageId: 'labResults.other_urine_analyses',
    },
    [LabCodes.MICROBIOLOGY]: {
      messageId: 'labResults.microbiology',
    },
    [LabCodes.CBC]: {
      messageId: 'labResults.cbc',
    },
    [LabCodes.LIPIDS]: {
      messageId: 'labResults.lipids',
    },
    [LabCodes.URINALYSIS]: {
      messageId: 'labResults.urinalysis',
    },
    [LabCodes.LIVER_FUNCTION]: {
      messageId: 'labResults.liver_function',
    },
    [LabCodes.CHEMISTRY]: {
      messageId: 'labResults.chemistry',
    },
    [LabCodes.GENETICS]: {
      messageId: 'labResults.genetics',
    },
    [LabCodes.OTHER]: {
      messageId: 'labResults.other',
    },
    [LabCodes.THYROID]: {
      messageId: 'labResults.thyroid',
    },
    [LabCodes.BLOOD_TRANSFUSION]: {
      messageId: 'labResults.blood_transfusion',
    },
    [LabCodes.COAGULATION]: {
      messageId: 'labResults.coagulation',
    },
    [LabCodes.CARDIAC]: {
      messageId: 'labResults.cardiac',
    },
    [LabCodes.ELECTROLYTES]: {
      messageId: 'labResults.electrolytes',
    },
    [LabCodes.ARTERIAL_BLOOD_GASES]: {
      messageId: 'labResults.arterial_blood_gases',
    },
  },
};

export const VITALL_CODE_MAP = {
  // Loinc
  '8462-4,8480-6,55284-4,85354-9,8716-3': BLOOD_PRESSURE,
  // Snomed
  '75367002,46973005,392570002,135840009': BLOOD_PRESSURE,
  // Icd10
  'Z01.30': BLOOD_PRESSURE,
};

export const METRIC_TYPES: Record<string, string> = {
  [AVG_HEART_RATE]: 'Avg Heart Rate',
  [BLOOD_GLUCOSE]: 'Blood Sugar',
  [BLOOD_PRESSURE]: 'Blood Pressure',
  systolic: 'Systolic',
  diastolic: 'Diastolic',
  [BODY_TEMPERATURE]: 'Temperature',
  [BODY_WEIGHT]: 'Body Weight',
  [BODY_HEIGHT]: 'Body Height',
  [DISTANCE]: 'Distance',
  [ENERGY_BURNED]: 'Energy Burned',
  [HEART_RATE]: 'Heart Rate',
  [RESPIRATION]: 'Respiration',
  [STEPS]: 'Steps',
  [SLEEP_DURATION]: 'Sleep Duration',
  [SLEEP_SCORE]: 'Sleep Score',
  [OXYGEN_SATURATION]: 'Oxygen Saturation',
  [PAIN]: 'Pain',
  [BODY_MASS_INDEX]: 'Body Mass Index',
  [MUSCLE_MASS]: 'Body Muscle Mass',
  [BODY_FAT_PERCENTAGE]: 'Body Fat Percentage',
  [CHEST_CM]: 'Chest',
  [WAIST_CM]: 'Waist',
  [HIPS_CM]: 'Hips',
  [THIGH_CM]: 'Thigh',
  [WAIST_HEIGHT_RATIO]: 'Waist Height Ratio',
  [WAIST_HIP_RATIO]: 'Waist Hip Ratio',
};

export function getObservationConfig(code: string): SignType | undefined {
  if (!(code in VITAL_SIGN_TYPES)) {
    throw new Error('Invalid code');
  }
  return VITAL_SIGN_TYPES[code];
}
